import { Spin } from 'antd';
import { BookCopy } from 'lucide-react';
import { Navigate, useSearchParams } from 'react-router';
import { ARRANGEMENT_TYPES, isArrangementType, type TemplateSetSummaryDto } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { TEMPLATES_COPY } from './copy';
import { useTemplateSets } from './templateQueries';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4] `/templates` — THE DOOR, AND A REDIRECT WHILE ONE SET EXISTS.
 *
 *  With one set this page forwards straight to its library. With several it lists them. `?publish=<type>`
 *  is the typed list's `Publish drafts`: the list knows the TYPE and not the set.
 *
 *  ⚠️ [amended DP2026] TWO BOOKLETS OF ONE EVENT NOW STAND SIDE BY SIDE (`durga-puja-2025`, `durga-puja-2026`),
 *  AND BOTH CREATE ORDERS OF THE SAME TYPE. So:
 *  - Each set is LABELLED BY YEAR with its PA number beneath, newest first, so nobody opens the wrong
 *    booklet in a hurry. The library itself is unchanged — one component, the set from the URL.
 *  - `?publish=` NO LONGER PICKS THE NEWEST SET. A publish run accepts only the drafts its own set made
 *    (`templateRun.service.ts`, `template_source.set_id`), so sending the button to 2026 would have left
 *    every draft made from 2025 unreachable from it. With one set of the event it forwards as before; with
 *    several it lists them, each row opening that booklet's Publish page.
 *
 *  ⚠️ [amended P7B.1 §4] A SIDEBAR ROW — "Templates", for a template author only (`templateAuthorsOnly`,
 *  `mayUseTemplatesAt`) — so this page has no `parent` link: it is a sidebar root. The route gate is still
 *  `RequireTemplateAuthor`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const newestFirst = (sets: readonly TemplateSetSummaryDto[]): TemplateSetSummaryDto[] =>
  [...sets].sort((a, b) => b.sourceYear - a.sourceYear || a.key.localeCompare(b.key));

const TemplatesPage = (): React.ReactElement => {
  const [searchParams] = useSearchParams();
  const sets = useTemplateSets();
  const publishParam = searchParams.get('publish');

  if (sets.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  const items = sets.data?.items ?? [];
  const publishType = isArrangementType(publishParam) ? publishParam : null;
  const ofEvent = publishType === null ? [] : items.filter((set) => set.event === ARRANGEMENT_TYPES[publishType].event);

  if (publishType !== null) {
    const [only] = ofEvent;
    if (ofEvent.length === 1 && only !== undefined) {
      return <Navigate to={`/templates/${only.key}/publish?type=${publishType}`} replace />;
    }
  }
  const [only] = items;
  if (items.length === 1 && only !== undefined && publishParam === null) {
    return <Navigate to={`/templates/${only.key}`} replace />;
  }

  /* Several booklets of the event to publish from: list only those, each opening its own Publish page. */
  const picking = publishType !== null && ofEvent.length > 1 ? publishType : null;
  const shown = newestFirst(picking === null ? items : ofEvent);

  return (
    <div className="flex flex-col gap-4" data-testid="template-sets">
      <PageHeader
        icon={BookCopy}
        title={TEMPLATES_COPY.setsTitle}
        subtitle={picking === null ? undefined : TEMPLATES_COPY.publishPick}
      />
      {items.length === 0 ? (
        <EmptyState title={TEMPLATES_COPY.noSets} description={TEMPLATES_COPY.noSetsBody} />
      ) : (
        <ul className="flex flex-col gap-3">
          {shown.map((set) => (
            <li
              key={set.id}
              className="rounded-xl border border-border bg-card px-4 py-3"
              data-testid={`template-set-${set.key}`}
            >
              <NameCell
                to={picking === null ? `/templates/${set.key}` : `/templates/${set.key}/publish?type=${picking}`}
                name={TEMPLATES_COPY.setYear(set.sourceYear)}
                secondary={TEMPLATES_COPY.setSource(set)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TemplatesPage;
